import styled from "styled-components";
import { card, navyCard } from "../../style/styled-components/mixins";
import CopyrightNote from "../../components/ui/specialty/CopyrightNote";
import { Link } from "react-router-dom";
import TitleTemplate from "../../components/ui/common/template/TitleTemplate";
import PageTemplate from "../../components/ui/common/template/PageTemplate";
import DiamondScope from "../../components/ui/specialty/DiamondScope";
import CompareTable from "../../components/ui/specialty/CompareTable";
import ContactPoints from "../../components/ui/members/ContactPoints";
import CenterDiv from "../../style/styled-components/components/CenterDiv";
import UseBreakpoints from "../../components/hooks/ui/UseBreakpoints";
import DevImage from "../../resources/images/dev.jpg";

// 근거: ASCP 시스템 구성도(홍보용, 2026-10). 순서·역할·모듈은 구성도 그대로 옮기고,
// 영문 명칭(full)만 ASCP 시스템 체계 정의서 v1(2026-09-05, 박찬욱 대표관세사)을 따른다.
const systems = [
  {
    name: "ACRIS",
    full: "AI Customs Risk Intelligence System",
    role: "판단·리스크 진단",
    desc: ["수출입 자료를 법령·해설서·결정례 근거로 판단", "위험 사전 진단"],
    modules: [
      { title: "지능형 품목분류", points: ["물품 설명으로 HS 10단위 판단", "근거 조문·결정례 기반 의견서 작성"] },
      { title: "심사·리스크 진단", points: ["신고 자료로 관세조사 위험 사전 진단", "점검 우선순위 제시"] },
      { title: "과세평가", points: ["수입물품 과세가격을 법령·판례 근거로 검토"] },
      { title: "외환 진단", points: ["수출입 신고·외환 송금 대사", "외국환거래법 위반 위험 점검"] },
      { title: "세관장확인 대상업무", points: ["수입 요건(검역·인증) 원스톱 확인"] },
      { title: "전략물자·FTA·자동차부품", points: ["수출통제 대상 확인", "FTA 원산지 판정", "자동차 부품 전문 품목분류"] },
    ],
  },
  {
    name: "ATLAS",
    full: "AONE Trade Law & Analysis System",
    role: "법령·근거 검색",
    desc: ["법령·판례·해석례 원문에서 근거 검색", "인용·원문 일치 검증"],
    modules: [
      { title: "법률 자문", points: ["관세·무역 법령 질의에 원문 근거 답변", "의견서 작성"] },
      { title: "학술 리서치", points: ["논문·권위 자료에서 근거 수집", "출처를 명기한 연구 보고서 작성"] },
      { title: "인용 검증", points: ["모든 인용 조문·판례를 원문과 대조", "근거 없는 답변 차단"] },
    ],
  },
  {
    name: "AXIS",
    role: "글로벌 통상 솔루션",
    desc: ["공급망·컴플라이언스 진단을 고객사 맞춤 솔루션으로 구성", "제안까지 연계"],
    modules: [
      { title: "글로벌 공급망", points: ["공급망 지도 시각화", "제재·수급 위험 분석", "대체 수급국가·해소 방안 도출"] },
      { title: "지능형 컴플라이언스", points: ["신고 정합성 진단", "환급·탄소국경조정(CBAM) 점검"] },
      { title: "제안·컨설팅", points: ["고객 맞춤 제안서 작성", "영문 회사 소개 작성"] },
    ],
  },
  {
    name: "CRM",
    full: "Customer Relationship Management",
    role: "고객 관리",
    desc: ["고객사별 정보 통합 관리", "영업 기회·위험 신호 제공"],
    modules: [
      { title: "고객사 정보", points: ["거래·계약·담당자·수출입 현황을 한 화면에 통합"] },
      { title: "자동 신호", points: ["공시·뉴스 일일 모니터링", "기회·위험 신호 자동 탐지"] },
      { title: "영업 파이프라인", points: ["제안부터 계약까지 단계별 관리"] },
    ],
  },
  {
    name: "TCMS",
    full: "Total Customs Management System",
    role: "통관 자동화",
    desc: ["서류에서 신고서까지 자동 작성", "신고 전 오류 차단"],
    modules: [
      { title: "통관 플랫폼", points: ["인보이스·포장명세서 자동 인식", "수입신고서 자동 작성"] },
      { title: "신고 전 검증", points: ["세번·요건·세액 오류 신고 전 점검·차단"] },
      { title: "전자상거래(특송)", points: ["특송 신고 전 위험 선별", "화주 리포트 제공"] },
    ],
  },
];
const engines = [
  { title: "지능형 품목분류 엔진", text: "관세율표 10단위까지 판단하고 근거 조문·결정례를 제시" },
  { title: "실시간 법적 근거", text: "인용 법령을 법제처 현행본과 자동 대조, 개정 전 조문 사용 차단" },
  { title: "근거 검색", text: "결정례·해설서·법령·판례에서 관련 원문을 찾아 연결" },
  { title: "규칙 엔진", text: "관세사가 확정한 판단 기준을 고정해 결과가 흔들리지 않게" },
];

const ASCPPage = () => {
  const breakpoints = UseBreakpoints();
  return (
    <PageTemplate
      large
      image={DevImage}
      title="ASCP 플랫폼"
      phrase="관세 실무 전문가가 직접 설계하고 검수하는 관세무역 AI 플랫폼, ASCP"
    >
      <p>
        ASCP(AONE Smart Customs Platform)는 에이원의 통관·컨설팅·법률 업무를 하나로 잇는 자체 플랫폼입니다.
        판단·리스크 진단(ACRIS), 법령·근거 검색(ATLAS), 글로벌 통상 솔루션(AXIS), 고객 관리(CRM), 통관 자동화(TCMS)
        5개 시스템이 같은 판단 엔진을 공유하고, 모든 결과는 관세사가 검토합니다.
      </p>

      <TitleTemplate
        title="시스템 구성"
        noImage
        subHeader="통관 실행부터 리스크 판단, 법적 근거, 고객 관리까지 한 플랫폼에서 이어집니다."
      />
      <SystemGrid breakpoints={breakpoints}>
        {systems.map((s) => (
          <div key={s.name}>
            <div className="name">{s.name}</div>
            {/* AXIS는 정식 영문 명칭이 아직 없어 빈 줄로 높이만 맞춘다 */}
            <div className="full">{s.full || " "}</div>
            <div className="role">{s.role}</div>
            <ul className="desc">
              {s.desc.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ul>
            <ul className="modules">
              {s.modules.map((m) => (
                <li key={m.title}>
                  <b>{m.title}</b>
                  <ul className="points">
                    {m.points.map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </SystemGrid>
      <EngineBar breakpoints={breakpoints}>
        <div>
          <b>공용 엔진</b>
          <span>화면 뒤에서 다섯 시스템이 함께 쓰는 핵심 기술 — 한 번 고치면 모든 서비스에 반영</span>
        </div>
        {engines.map((e) => (
          <div key={e.title}>
            <b>{e.title}</b>
            <span>{e.text}</span>
          </div>
        ))}
      </EngineBar>
      <Note>
        다섯 시스템은 판단 엔진을 따로 두지 않고 하나의 소스를 함께 씁니다. 그래서 어느 업무에서든 같은 기준과 같은 근거로 판단합니다.
        경영·규제 동향은 5개 시스템과 별도로 운영되는 오늘의 브리핑 채널에서 정리합니다.{" "}
        <Link to="/boards/briefing">오늘의 브리핑 보기 →</Link>
      </Note>

      <TitleTemplate
        title="Strength"
        noImage
        subHeader="실제로 돌아가는 서비스와 공개된 실적이 에이원의 기술력을 증명합니다."
      />
      <DiamondScope
        contents={[
          { title: "국내 최초", text: "2024 지능형 기반 HS 품목분류 추천시스템 개발" },
          { title: "국가 R&D", text: "2024 과학기술정보통신부 인공지능 활용 지원사업 선정" },
          { title: "특허 출원", text: "HS CODE 품목분류 정보 제공 시스템 (제10-2024-0155229호)" },
          { title: "전문가 검수", text: "관세청 출신 품목분류 전문가가 문항 단위로 검수" },
        ]}
      />

      <TitleTemplate
        title="일하는 방식의 차이"
        noImage
        subHeader="같은 업무도 플랫폼 위에서는 더 빠르고, 근거가 남습니다."
      />
      <CenterDiv>
        <CompareTable
          contents={[
            { div: "품목분류", content1: "담당자 경험에 의존", content2: "AI 후보 + 해설서·판례 근거 + 관세사 확정" },
            { div: "관세심사 대비", content1: "자료를 표로 일일이 대사", content2: "신고·외환 자료 업로드 → 6개 부문 자동 점검 보고서" },
            { div: "규제 변경", content1: "사후 뉴스레터 안내", content2: "오늘의 브리핑으로 주요 동향 정리, 영향 품목 사전 점검" },
            { div: "공급망 리스크", content1: "개별 문의 대응", content2: "BOM 단위 수출통제·제재·강제노동 규정 스크리닝" },
            { div: "진행 현황", content1: "전화·메일 문의", content2: "ACIP 고객포털에서 신고·컨설팅 현황 확인" },
          ]}
        />
      </CenterDiv>

      <TitleTemplate
        title="Contact Point"
        noImage
        subHeader="플랫폼 도입과 연동은 IT 총괄 대표관세사가 직접 상담합니다."
      />
      <ContactPoints members={["cupark"]} noConsult />
      <CopyrightNote />
    </PageTemplate>
  );
};
export default ASCPPage;

const SystemGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ breakpoints }) => (breakpoints.xl ? "repeat(5, minmax(0, 1fr))" : breakpoints.md ? "repeat(2, minmax(0, 1fr))" : "minmax(0, 1fr)")};
  gap: 1rem;
  & > div {
    ${card}
    position: relative;
    overflow: hidden;
    padding: 1.75rem 1.25rem 0.5rem;
    &:before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: linear-gradient(90deg, ${({ theme }) => theme.colors.accent}, ${({ theme }) => theme.colors.dark});
    }
    & > .name {
      font-size: 1.75rem;
      font-weight: 800;
      color: ${({ theme }) => theme.colors.dark};
      letter-spacing: 0.02em;
    }
    & > .full {
      font-size: 0.8rem;
      line-height: 1.3;
      /* 영문 명칭이 두 줄로 접히는 카드와 역할 줄 높이를 맞춘다 */
      min-height: 2.6em;
      color: ${({ theme }) => theme.colors.foreground.dimmed};
    }
    & > .role {
      font-size: 1.05rem;
      font-weight: 700;
      color: ${({ theme }) => theme.colors.accent};
      padding: 0.9rem 0 0.4rem 0;
    }
    /* 개조식 줄 앞에 찍는 점 */
    & > .desc > li,
    & .points > li {
      position: relative;
      padding-left: 0.8em;
      &::before {
        content: "•";
        position: absolute;
        left: 0;
        color: ${({ theme }) => theme.colors.accent};
      }
    }
    & > .desc {
      list-style: none;
      margin: 0 0 0.75rem;
      padding: 0;
      font-size: 0.875rem;
      line-height: 1.6;
      /* 5열일 때 줄 수가 다른 카드끼리도 첫 모듈 줄을 맞춘다 */
      min-height: ${({ breakpoints }) => (breakpoints.xl ? "4.8em" : "0")};
      color: ${({ theme }) => theme.colors.monotone.dark};
    }
    & > .modules {
      list-style: none;
      margin: 0;
      padding: 0;
      & > li {
        padding: 0.9rem 0 1rem;
        border-top: 1px solid ${({ theme }) => theme.colors.line};
        & > b {
          font-size: 0.95rem;
          color: ${({ theme }) => theme.colors.dark};
        }
        & > .points {
          list-style: none;
          margin: 0.3rem 0 0;
          padding: 0;
          font-size: 0.8rem;
          line-height: 1.55;
          color: ${({ theme }) => theme.colors.foreground.dimmed};
        }
      }
    }
  }
`;
const EngineBar = styled.div`
  ${navyCard}
  display: grid;
  grid-template-columns: ${({ breakpoints }) => (breakpoints.xl ? "repeat(5, minmax(0, 1fr))" : breakpoints.md ? "repeat(2, minmax(0, 1fr))" : "minmax(0, 1fr)")};
  margin-top: 1rem;
  overflow: hidden;
  & > div {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    padding: 1.1rem 1.25rem;
    /* 칸 사이 구분선: 왼쪽·위로 1px 그림자를 두고 바깥쪽은 overflow로 잘라 줄이 바뀌어도 안쪽에만 남긴다 */
    box-shadow: -1px -1px 0 rgba(255, 255, 255, 0.12);
    &:first-child {
      grid-column: ${({ breakpoints }) => (breakpoints.md && !breakpoints.xl ? "1 / -1" : "auto")};
      background-color: rgba(255, 255, 255, 0.1);
      & > b {
        font-weight: 800;
      }
    }
    & > span {
      font-size: 0.8rem;
      line-height: 1.55;
      color: rgba(255, 255, 255, 0.75);
    }
  }
`;
const Note = styled.p`
  font-size: 1.05em !important;
  color: ${({ theme }) => theme.colors.foreground.dimmed};
  padding-top: 1.5em !important;
  & a {
    color: ${({ theme }) => theme.colors.accent};
    font-weight: bold;
  }
`;
